(ns frontend.view.grid.holdings-grid.events
  (:require
   [frontend.view.grid.holdings-grid.db :as db]
   [frontend.view.grid.holdings-grid.columns :as columns]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.local-storage :as ls]
   [frontend.view.grid.companies-grid.events :as companies-events]
   [frontend.view.grid.real-estates-grid.events :as real-estates-events]
   [frontend.view.grid.fof-assets-grid.events :as fof-assets-events]
   [frontend.view.grid.other-assets-grid.events :as other-assets-events]
   [frontend.routing :as routing]
   [common.localization :as loc]
   [frontend.view.modals.events :as modals]
   [potpuri.core :refer [index-by fn-> fn->>]]
   [frontend.state.current-session :as session]
   [frontend.actions.named-view :as named-view-actions]
   [common.schema.registry :as registry]
   [common.schema.entity-type-names :as entity-type-names]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.conn :as conn]
   [promesa.core :as p]
   [frontend.view.modals.confirmation.view :as confirmation]
   [clojure.data :as data]
   [reagent.core :as r]
   [frontend.view.entity :as f-entity]
   [frontend.state.permissions :as f-permissions]
   [common.schema.entity-types :as entity-types]
   [common.util :as cu]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :holdings-view-settings]
   rest))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/holding)
           (remove (fn [[k v]]
                     (let [grid-visibility (->> v
                                                :field/default-visibility
                                                :grid)]
                       (or (nil? grid-visibility)
                           (= grid-visibility :editor.type/none)
                           (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                                   (not (:field/secondary-field? v)))
                             (not (f-permissions/field-read-access? k :entity.type/holding nil))
                             (not (f-permissions/field-read-access? (:field/related-fields v)
                                                                    :entity.type/holding
                                                                    nil)))))))
           (sort-by (fn [[k v]]
                      (-> v
                          :field/translations
                          :en)))))
        asset-types (session/holding-target-asset-types)
        allowed-asset-types (set asset-types)
        asset-type-schema (fn [entity-type]
                            (when (and (some #{entity-type} allowed-asset-types)
                                       (or recalculate?
                                           (not calculated)))
                              (->> (case entity-type
                                     :entity.type/company
                                     (companies-events/precalculated-schema recalculate?)
                                     :entity.type/real-estate
                                     (real-estates-events/precalculated-schema recalculate?)
                                     :entity.type/fof-asset
                                     (fof-assets-events/precalculated-schema recalculate?)
                                     :entity.type/other-asset
                                     (other-assets-events/precalculated-schema recalculate?)
                                     nil))))
        asset-schema (->> asset-types
                          (mapcat asset-type-schema)
                          (map (fn [[k v]]
                                 [[:holding/target-asset k] v]))
                          (into {})
                          (remove (fn-> first (= [:holding/target-asset :asset/name])))
                          (sort-by (fn [[_ v]]
                                     (-> v
                                         :field/translations
                                         :en)))
                          doall)

        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (concat schema asset-schema))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn holding-column-keys
  []
  (concat
   (when (session/is-admin?) [:db/id])
   (map first (keys-and-schemas))))

(def default-kanban-attributes [:holding/valuation-comment :holding/exit-plan])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set
         (concat @db/selected-columns
                 attributes
                 default-kanban-attributes
                 [footer-1 footer-2 (entity-types/type->owner-attribute :entity.type/holding)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

(defn column-tabs
  []
  (let [target-asset-types (session/holding-target-asset-types)]
    (->> (concat [:entity.type/holding]
                 (when (> (count target-asset-types) 1)
                   [:entity.type/asset])
                 target-asset-types)
         (map (fn [entity-type]
                (let [entity-type-name (-> entity-type-names/entities
                                           (get entity-type)
                                           :field/translations
                                           :en)]
                  {:key   entity-type
                   :label (loc/tr :entity-type-fields [entity-type-name])}))))))

(defn resolve-tab
  [field]
  (let [target-asset-types (session/holding-target-asset-types)
        ref-field? (and (vector? field)
                        (keyword? (first field))
                        (keyword? (second field)))
        [ref attr] (when ref-field?
                     field)]
    (cond
      (keyword? field)
      :entity.type/holding
      (and ref-field?
           (= (count target-asset-types) 1))
      (first target-asset-types)
      (and ref-field?
           (= (namespace attr) "asset"))
      :entity.type/asset
      (and ref-field?
           (= (namespace attr) "common"))
      :entity.type/asset
      (and ref-field?
           (= (namespace attr) "company"))
      :entity.type/company
      (and ref-field?
           (= (namespace attr) "real-estate"))
      :entity.type/real-estate
      (and ref-field?
           (= (namespace attr) "fof-asset"))
      :entity.type/fof-asset
      (and ref-field?
           (= (namespace attr) "other-asset"))
      :entity.type/other-asset
      :else :unknown)))

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/holdings
   :entity-type :entity.type/holding
   :modal-state db/modal-state
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/holding-filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :selected-pipeline nil
   :ls-path ls-path
   :kanban? true
   :kanban-args {:default-grouped-by  :holding/exit-status
                 :kanban-state        db/kanban
                 :grouped-by-state    db/grouped-by
                 :card-settings-state db/card-settings
                 ;;for long card view:
                 :default-attributes  default-kanban-attributes
                 :expanded-state      db/expanded
                 :sorted-kanban-entities-state db/sorted-kanban-entities
                 :lists-state         db/lists
                 :expanded?           expanded?
                 :toggle-expand!      toggle-expand!}
   :column-tabs (column-tabs)
   :resolve-tab resolve-tab

   :column-keys holding-column-keys
   :default-columns columns/default-columns
   :mandatory? columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (grid/resolve-column-key-universe (setup-args)))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/holdings)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/holdings)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/holdings)]
    (r/dispose! tracker))

  (swap! db/holdings dissoc
    :loaded
    :schema
    :entities
    :entity-types
    :view-settings
    :sorted-grid-entities
    :search
    :selected
    :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/holdings update :show-quick-filters? not))

(defn system-views
  []
  [{:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :holding/name
                          :sort-direction   "ASC"
                          :view-mode        :grid-mode}
    :named-view/name (loc/tr :all-holdings)
    :disable-delete? true
    :db/id "named-view-all-holdings"}
   {:named-view/settings {:selected-columns columns/my-holdings-columns
                          :ordered-columns  columns/my-holdings-ordered-columns
                          :view-mode        :grid-mode
                          :sort-by          :holding/name
                          :sort-direction   "ASC"
                          :filters          {:holding/owner {:operand :operand/any-of
                                                             :lower-bound-value
                                                             #{(:db/id @session/account)}}}}
    :named-view/name (loc/tr :my-holdings)
    :disable-delete? true
    :db/id "named-view-my-holdings"}
   {:named-view/settings {:selected-columns columns/default-columns
                          :ordered-columns  columns/ordered-default-columns
                          :sort-by          :holding/name
                          :sort-direction   "ASC"
                          :view-mode        :board-mode}
    :named-view/name (loc/tr :holdings-pipeline)
    :disable-delete? true
    :db/id "named-view-holdings-pipeline"}])

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :holdings)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn holdings-updates
  []
  (swap! db/holdings assoc :grid-entities @session/all-holdings))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/holdings assoc :loaded true)
                    (deref db/holdings))))))

(defn setup
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/holdings assoc
      :tracker-1 (r/track! holdings-updates)
      :tracker-2 (r/track! permission-updates)
      :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :holdings
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn mass-delete!
  []
  (-> (conn/sendp! :holding/mass-action
                   {:action      :delete-holdings
                    :holding-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [args]
                (swap! db/holdings assoc :selected {})))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected holdings" e)))))

(defn open-selected!
  [_]
  (let [holding-id (-> (grid/get-selected-items (setup-args))
                       (first))
        data (get @session/entities holding-id)]
    (routing/open-entity (:db/id data))))

(defn delete-selected-clicked
  [_]
  (modals/open-modal!
   confirmation/modal-key
   {:body          [:div
                    [:p (loc/tr :confirm-delete-selected)]]
    :confirm-text  (loc/tr :confirm-delete-selected-button)
    :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
    :on-confirm    #(mass-delete!)}))

(defn show-collapse-all?
  []
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual-count (count @db/expanded)
        total-entity-count (count @db/sorted-kanban-entities)]
    (or (and (= card-mode :short-card)
             (> individual-count 0))
        (and (= card-mode :long-card)
             (< individual-count total-entity-count)))))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))
